package org.multiplatform.kotfolio.pages.kotlin.new_feature

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/kotlin/new_feature/2.0.20")
@Composable
fun NewFeature2_0_20Page() {
    BlogLayout(
        PageProperties(
            "Kotlin 2.0.20",
            ""
        ),
        BlogProperties("kotlin", "Kotlin 2.0.20")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Internal Optimizations",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Context Passing: More dynamic handling of injected parameters, improving the resolution order based on definitions, parameters, and scope.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Thread-Safe Collections: Improved internal collections for better stability in concurrent environments.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Kotlin Time API:  Use the Kotlin standard Time API instead of old internals, simplifying internal architecture.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Data Class Copy Function Visibility",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The copy function in data classes now inherits the visibility of the primary constructor. Previously, the copy function was always public, regardless of the constructor's visibility, which could lead to unintended usage.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Creating a data class with a private constructor could cause the automatically generated copy() function to have different visibility than expected. Kotlin 2.0.20 aims to improve consistency by gradually making the copy() function’s default visibility the same as the constructor. If you have a problem with the copy function, be sure to update it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    data class User(private val name: String)

                    fun main() {
                        val user = User("Alice")
                        // This line would compile even though the constructor is private
                        val copiedUser = user.copy(name = "Bob")
                    }
                    
                    //After Kotlin 2.0.20
                    data class User(private val name: String)

                    // This will now cause a compilation error due to private visibility
                    val copiedUser = user.copy(name = "Bob")
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class User private constructor(
                        val name: String,
                        val age: Int
                    ) {
                        companion object {
                            fun create(name: String, age: Int): User {
                                require(age in 0..200)
                                return User(name, age)
                            }
                        }
                    }
                    
                    val user = User.create(name = "Test", age = 20)
                    val newUser = user.copy(age = -1)
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class PositiveInteger private constructor(val number: Int) {
                        companion object {
                            fun create(number: Int): PositiveInteger? = 
                                if (number > 0) PositiveInteger(number) else null
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Opt-in to new behavior
                    @ConsistentCopyVisibility
                    data class PositiveInteger private constructor(val number: Int)

                    // Opt-out to suppress warnings
                    @ExposedCopyVisibility
                    data class PositiveInteger private constructor(val number: Int)
                    """.trimIndent()
            )

            SpanText(
                text = "Static Accessors for Source Sets in Multiplatform Projects",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "With static accessors, managing source sets in multiplatform projects is now more straightforward. Simply declare your targets, and Kotlin will provide type-safe accessors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    kotlin {
                        jvm()
                        ios()

                        sourceSets {
                            val commonMain by getting {
                                dependencies {
                                    implementation("...")
                                }
                            }
                            val jvmMain by getting
                            val iosMain by getting
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    kotlin {
                        jvm()
                        linuxX64()

                        sourceSets {
                            commonMain.languageSettings {
                                progressiveMode = true
                            }

                            jvmMain { /* ... */ }
                            linuxX64Main { /* ... */ }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Concurrent Marking in Kotlin/Native Garbage Collector",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Concurrent marking has been added to the Kotlin/Native garbage collector, allowing the GC to run concurrently with the program. This results in reduced pause times and better performance in memory-intensive applications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //gradle.properties
                    
                    kotlin.native.binary.gc=cms
                    """.trimIndent()
            )

            SpanText(
                text = "Improvements in the Compose Compiler",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Fixes for unnecessary recompositions in multiplatform projects.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "New ways to configure compiler options using feature flags.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Support for default parameters in abstract composable functions.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Set feature flags in your build.gradle.kts
                    composeCompiler {
                        featureFlags = setOf(
                            ComposeFeatureFlag.IntrinsicRemember.disabled(),
                            ComposeFeatureFlag.OptimizeNonSkippingGroups
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "@ExperimentalWasmDsl Annotation Location Change",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The @ExperimentalWasmDsl annotation has been relocated in Kotlin/Wasm, important for developers working with WebAssembly (Wasm) in Kotlin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    import kotlin.wasm.ExperimentalWasmDsl

                    @ExperimentalWasmDsl
                    fun wasmFunction() {
                        // WebAssembly-specific code
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "New Option for Sharing JVM Artifacts as Class Files",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A new feature allows JVM artifacts to be shared between Gradle projects as class files instead of JAR files, which can enhance build performance.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    tasks.withType<Jar> {
                        archiveClassifier.set("classes")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Compose Compiler Update",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Compose compiler has been updated to align with Kotlin 2.0.20, ensuring compatibility and performance improvements for developers using Jetpack Compose and Compose Multiplatform Mobile.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    dependencies {
                        implementation("androidx.compose.compiler:compiler:1.5.0")
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Support for UUIDs in the Common Kotlin Standard Library",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import kotlin.random.Random
                    import java.util.UUID

                    fun generateUUID(): String {
                        return UUID.randomUUID().toString()
                    }

                    fun main() {
                        println(generateUUID())
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val uuid1 = Uuid.parse("550e8400-e29b-41d4-a716-446655440000")
                    val randomUuid = Uuid.random()

                    println(uuid1)
                    // Output: 550e8400-e29b-41d4-a716-446655440000

                    println(uuid1 == randomUuid)
                    // Output: false
                    """.trimIndent()
            )

            SpanText(
                text = "Base64 Decoder Changes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // Configure Base64 padding behavior
                    val base64 = Base64.UrlSafe.withPadding(Base64.PaddingOption.PRESENT)
                    val encoded = base64.encode("data".toByteArray())
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Extensible Data Arguments (data objects)
                    
                    // Before (lots of parameters)
                    fun myFunction(param1: Int = 0, param2: String = "", param3: Boolean = false) {
                        // ...
                    }

                    // After (using data object)
                    data object MyFunctionOptions {
                        var param1: Int = 0
                        var param2: String = ""
                        var param3: Boolean = false
                    }

                    fun myFunction(options: MyFunctionOptions = MyFunctionOptions) {
                        // Access options.param1, options.param2, etc.
                    }

                    fun MyCallerFunction(){
                        // Calling the function
                        myFunction(param1 = 10, param3 = true) // No need to create MyFunctionOptions object
                    }
                    """.trimIndent()
            )
        }
    }
}





























